import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { openPdf } from "@/lib/pdf";
import { toast } from "sonner";
import { Search, Coins, FileText, Ticket, XCircle, Check, Film, Repeat, X, Printer, Loader2, Bookmark } from "lucide-react";

const PAY = [
  { v: "bar", l: "Bar" },
  { v: "karte", l: "Karte" },
  { v: "gutschein", l: "Gutschein" },
  { v: "einladung", l: "Einladung" },
  { v: "frei", l: "Kostenlos" },
  { v: "mitglied", l: "Mitgliedskarte" },
];

const STATE_STYLES = {
  reserviert: "border-[#D4AF37] text-[#D4AF37]",
  bezahlt: "border-[#4CAF50] text-[#4CAF50]",
  storniert: "border-[#7A1921] text-[#7A1921]",
};

export default function Reservations() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("offen");
  const [checkoutTarget, setCheckoutTarget] = useState(null);
  const [rebookTarget, setRebookTarget] = useState(null);

  const load = () => api.get("/reservations").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    if (stateFilter === "offen" && r.state !== "reserviert") return false;
    if (stateFilter === "bezahlt" && r.state !== "bezahlt") return false;
    if (stateFilter === "storniert" && r.state !== "storniert") return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (r.guest_name || "").toLowerCase().includes(q)
      || (r.guest_email || "").toLowerCase().includes(q)
      || (r.movie_title || "").toLowerCase().includes(q)
      || (r.seats || []).join(",").toLowerCase().includes(q);
  }), [rows, filter, stateFilter]);

  return (
    <div className="space-y-6" data-testid="reservations-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Vorgänge</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">Reservierungen</h1>
          <div className="text-xs text-[#A8A398] mt-2">
            {rows.filter((r) => r.state === "reserviert").length} offen ·{" "}
            {rows.filter((r) => r.state === "bezahlt").length} kassiert ·{" "}
            {rows.filter((r) => r.state === "storniert").length} storniert
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {["offen", "bezahlt", "storniert", "alle"].map((s) => (
            <button key={s}
              data-testid={`reservations-filter-${s}`}
              onClick={() => setStateFilter(s)}
              className={`text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 rounded-full border transitions-safe ${
                stateFilter === s ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]/50"
              }`}>{s}</button>
          ))}
          <div className="relative w-full sm:w-auto">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#575148]" />
            <input value={filter} onChange={(e) => setFilter(e.target.value)}
              data-testid="reservations-search"
              placeholder="Gast, Film, Sitz …"
              className="pl-9 pr-3 py-2 bg-[#050505] border border-[#2A2A2A] rounded text-sm focus:border-[#D4AF37] outline-none w-full sm:w-72" />
          </div>
        </div>
      </div>

      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-[#0A0A0A] border-b border-[#1A1A1A] text-[#A8A398] text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Erstellt</th>
              <th className="text-left p-3">Gast</th>
              <th className="text-left p-3">Film · Vorstellung</th>
              <th className="text-left p-3">Sitze</th>
              <th className="text-left p-3">Summe</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-[#141414] hover:bg-[#0C0C0C]/60" data-testid={`reservation-row-${r.id}`}>
                <td className="p-3 font-mono text-xs text-[#A8A398] whitespace-nowrap">
                  {r.created_at?.slice(0, 16).replace("T", " ")}
                </td>
                <td className="p-3">
                  <div className="font-medium text-[#F5F0E6]">{r.guest_name || "—"}</div>
                  <div className="text-[10px] text-[#575148]">{r.guest_email || ""}</div>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    {r.movie_poster_url && <img src={r.movie_poster_url} alt="" className="w-8 h-12 object-cover rounded" onError={(e)=>{e.currentTarget.style.display="none";}} />}
                    <div className="min-w-0">
                      <div className="truncate max-w-[220px]">{r.movie_title}</div>
                      <div className="text-[10px] text-[#A8A398] font-mono">
                        {r.showtime_date ? new Date(r.showtime_date + "T00:00:00").toLocaleDateString("de-DE") : "—"} · {r.showtime_start || ""} · {r.showtime_hall || ""}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="p-3 font-mono text-xs">
                  {(r.seats || []).join(", ") || "—"}
                  <span className="text-[10px] text-[#575148] ml-2">({r.tickets?.length || 0})</span>
                </td>
                <td className="p-3 font-mono">
                  {Number(r.total || 0).toFixed(2)} €
                </td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2.5 py-0.5 ${STATE_STYLES[r.state] || "border-[#2A2A2A] text-[#A8A398]"}`}>
                    {r.state}
                  </span>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {r.state === "reserviert" && (
                    <button onClick={() => setCheckoutTarget(r)}
                      data-testid={`reservation-checkout-${r.id}`}
                      className="text-xs px-3 py-1.5 border border-[#D4AF37] text-[#D4AF37] rounded hover:bg-[#D4AF37] hover:text-black transitions-safe inline-flex items-center gap-1.5 mr-2">
                      <Coins size={12} /> Kassieren
                    </button>
                  )}
                  {(r.state === "reserviert" || r.state === "bezahlt") && (
                    <button onClick={() => setRebookTarget(r)}
                      data-testid={`reservation-rebook-${r.id}`}
                      className="text-xs px-3 py-1.5 border border-[#EEDC82]/60 text-[#EEDC82] rounded hover:bg-[#EEDC82]/10 transitions-safe inline-flex items-center gap-1.5 mr-2">
                      <Repeat size={12} /> Umbuchen
                    </button>
                  )}
                  {r.tickets?.[0] && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${r.tickets[0].id}/pdf`} target="_blank" rel="noreferrer"
                      onClick={(e) => { e.preventDefault(); openPdf(`/tickets/${r.tickets[0].id}/pdf`); }}
                      className="text-xs px-2 py-1.5 border border-[#2A2A2A] rounded hover:border-[#D4AF37] inline-flex items-center gap-1 mr-2">
                      <FileText size={12} /> PDF
                    </a>
                  )}
                  <ReservedSignButtons reservation={r} />
                  {r.cancel_token && r.state === "reserviert" && (
                    <a href={`/reservierung/${r.cancel_token}`} target="_blank" rel="noreferrer"
                      className="text-xs px-2 py-1.5 border border-[#2A2A2A] rounded hover:border-[#D4AF37] inline-flex items-center gap-1">
                      <Ticket size={12} /> Öffnen
                    </a>
                  )}
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={7} className="p-10 text-center text-[#A8A398]">
                <Film size={28} className="mx-auto text-[#D4AF37]/60 mb-3" />
                Keine Reservierungen in dieser Ansicht
              </td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      {checkoutTarget && (
        <CheckoutDialog
          reservation={checkoutTarget}
          onClose={() => setCheckoutTarget(null)}
          onDone={() => { setCheckoutTarget(null); load(); }}
        />
      )}
      {rebookTarget && (
        <RebookDialog
          reservation={rebookTarget}
          onClose={() => setRebookTarget(null)}
          onDone={() => { setRebookTarget(null); load(); }}
        />
      )}
    </div>
  );
}

function ReservedSignButtons({ reservation }) {
  const [busy, setBusy] = useState(false);
  const build = () => {
    const seats = (reservation.seats || []).join(" · ") || "—";
    const timePart = [reservation.showtime_start, reservation.movie_title].filter(Boolean).join(" · ").slice(0, 60);
    return {
      entries: [{
        seat: seats,
        name: reservation.guest_name || "Gast",
        note: timePart,
      }],
    };
  };
  const preview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/signage/reserved-signs", build(), { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler"); }
    finally { setBusy(false); }
  };
  const printOff = async () => {
    if (!window.confirm(`Reserviert-Schild für „${reservation.guest_name || "Gast"}" an den Bürodrucker senden?`)) return;
    setBusy(true);
    try {
      await api.post("/admin/signage/reserved-signs?print_office=true", build());
      toast.success("An Drucker gesendet");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bürodrucker nicht erreichbar"); }
    finally { setBusy(false); }
  };
  return (
    <div className="inline-flex rounded border border-[#2A2A2A] overflow-hidden mr-2">
      <button onClick={preview} disabled={busy}
        data-testid={`reservation-sign-preview-${reservation.id}`}
        title="Reserviert-Schild als PDF"
        className="text-xs px-2 py-1.5 text-[#EE9CA1] hover:bg-[#EE9CA1]/10 disabled:opacity-40 flex items-center gap-1">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Bookmark size={12} />}
      </button>
      <button onClick={printOff} disabled={busy}
        data-testid={`reservation-sign-print-${reservation.id}`}
        title="Reserviert-Schild an Bürodrucker"
        className="text-xs px-2 py-1.5 text-[#D4AF37] border-l border-[#2A2A2A] hover:bg-[#D4AF37]/10 disabled:opacity-40 flex items-center gap-1">
        <Printer size={12} />
      </button>
    </div>
  );
}

function RebookDialog({ reservation, onClose, onDone }) {
  const [showtimes, setShowtimes] = useState([]);
  const [newStId, setNewStId] = useState(reservation.showtime_id || "");
  const [newSeats, setNewSeats] = useState((reservation.seats || []).join(", "));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/showtimes").then((r) => setShowtimes(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);

  const seatCount = (reservation.tickets?.length) || (reservation.seats?.length) || 0;
  const seatArr = newSeats.split(/[\s,;]+/).map((s) => s.trim().toUpperCase()).filter(Boolean);
  const seatCountOk = seatArr.length === seatCount;
  const isSameShowtime = newStId === reservation.showtime_id;
  const seatsChanged = seatArr.join("|") !== (reservation.seats || []).join("|").toUpperCase();

  const submit = async () => {
    if (!seatCountOk) return toast.error(`Genau ${seatCount} Sitze angeben`);
    if (!newStId) return toast.error("Bitte Vorstellung wählen");
    if (isSameShowtime && !seatsChanged) return toast.error("Keine Änderung — Sitze oder Vorstellung anpassen");
    setBusy(true);
    try {
      const { data } = await api.post(`/reservations/${reservation.id}/rebook`, {
        new_showtime_id: isSameShowtime ? null : newStId,
        new_seats: seatArr,
        reason: reason || "",
      });
      toast.success(`Umgebucht · TSE #${data.tse?.counter}`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Umbuchung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const chosenSt = showtimes.find((s) => s.id === newStId);

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="rebook-dialog">
      <div className="card-lux w-full max-w-xl overflow-hidden max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#1A1A1A] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#EEDC82] flex items-center gap-2"><Repeat size={12} /> Umbuchung ohne Storno</div>
            <div className="font-heading text-2xl mt-1">{reservation.guest_name || "—"} · {seatCount} Sitz(e)</div>
          </div>
          <button onClick={onClose} className="text-[#F5F0E6]/60 hover:text-[#EE9CA1]"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-[11px] text-[#A8A398]">
            Ursprünglich: <span className="text-[#EEDC82]">{reservation.movie_title}</span> · {reservation.showtime_date} · {reservation.showtime_start} · Sitze <span className="font-mono text-[#EEDC82]">{(reservation.seats || []).join(", ")}</span>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Neue Vorstellung</label>
            <select value={newStId} onChange={(e) => setNewStId(e.target.value)}
              data-testid="rebook-new-showtime"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none">
              {showtimes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.date} · {s.start_time} · {s.movie?.title_de || s.movie_title || "—"} · {s.hall}
                </option>
              ))}
            </select>
            {chosenSt && (
              <div className="text-[10px] text-[#575148] mt-1">
                {chosenSt.available_seats ?? "—"} Sitze frei · {chosenSt.hall}
              </div>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Neue Sitze ({seatCount} Stück, kommagetrennt)</label>
            <input type="text" value={newSeats} onChange={(e) => setNewSeats(e.target.value)}
              placeholder="z. B. A5, A6, A7"
              data-testid="rebook-new-seats"
              className={`w-full mt-1 bg-[#050505] border rounded px-3 py-2 font-mono focus:border-[#D4AF37] outline-none ${seatCountOk ? "border-[#2A2A2A]" : "border-[#7A1921]"}`} />
            <div className={`text-[10px] mt-1 uppercase tracking-widest ${seatCountOk ? "text-[#575148]" : "text-[#EE9CA1]"}`}>
              {seatArr.length} / {seatCount} eingegeben
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Grund (optional)</label>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="z. B. Kunden-Wunsch, Vorstellung ausgefallen"
              data-testid="rebook-reason"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
          </div>

          <div className="p-3 rounded border border-[#EEDC82]/30 bg-[#EEDC82]/5 text-[10px] text-[#EEDC82]">
            <b>Auswirkungen:</b> Alte Sitze werden frei, neue reserviert · Kein Geldfluss ·
            Neue Ticket-PDFs müssen ggf. neu gedruckt/versendet werden · TSE-Auditierungs-Signatur wird angelegt.
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#1A1A1A] flex gap-2">
          <button onClick={onClose}
            className="flex-1 border border-[#2A2A2A] text-[#A8A398] px-4 py-3 rounded text-[11px] uppercase tracking-[0.4em] hover:border-[#D4AF37] hover:text-[#F5F0E6]">
            Abbrechen
          </button>
          <button onClick={submit} disabled={busy || !seatCountOk} data-testid="rebook-confirm"
            className="flex-1 bg-[#EEDC82] hover:bg-[#F5E9A3] text-black px-4 py-3 rounded text-[11px] uppercase tracking-[0.4em] disabled:opacity-40 inline-flex items-center justify-center gap-2">
            <Repeat size={14} /> {busy ? "Buche um …" : "Umbuchung bestätigen"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CheckoutDialog({ reservation, onClose, onDone }) {
  const [payment, setPayment] = useState("bar");
  const [voucher, setVoucher] = useState("");
  const [discount, setDiscount] = useState(0);
  const [busy, setBusy] = useState(false);

  const gross = reservation.total || 0;
  const total = Math.max(0, +(gross - (parseFloat(discount) || 0)).toFixed(2));

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/reservations/${reservation.id}/checkout`, {
        payment_method: payment,
        voucher_code: voucher || null,
        discount: parseFloat(discount) || 0,
      });
      toast.success(`Kassiert · ${data.tickets_paid} Ticket(s) · ${data.total.toFixed(2)} €`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kassieren fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="checkout-dialog">
      <div className="card-lux w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#1A1A1A]">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Kasse</div>
          <div className="font-heading text-2xl mt-1">Reservierung kassieren</div>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex items-center gap-4 border border-[#1A1A1A] rounded p-3 bg-[#0A0A0A]">
            {reservation.movie_poster_url && (
              <img src={reservation.movie_poster_url} alt="" className="w-14 h-20 object-cover rounded" onError={(e)=>{e.currentTarget.style.display="none";}} />
            )}
            <div className="min-w-0">
              <div className="font-heading text-lg truncate">{reservation.movie_title}</div>
              <div className="text-[10px] text-[#A8A398] font-mono mt-1">
                {reservation.showtime_date ? new Date(reservation.showtime_date + "T00:00:00").toLocaleDateString("de-DE") : ""} · {reservation.showtime_start} · {reservation.showtime_hall}
              </div>
              <div className="text-xs text-[#F5F0E6] mt-1">{reservation.guest_name} · {(reservation.seats || []).join(", ")}</div>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Zahlungsart</label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {PAY.map((p) => (
                <button key={p.v} type="button"
                  data-testid={`checkout-pay-${p.v}`}
                  onClick={() => setPayment(p.v)}
                  className={`text-xs uppercase tracking-widest py-2 rounded border transitions-safe ${
                    payment === p.v ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]/50"
                  }`}>{p.l}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Gutschein-Code</label>
              <input value={voucher} onChange={(e) => setVoucher(e.target.value.toUpperCase())}
                data-testid="checkout-voucher"
                placeholder="MOV-XXXX"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none uppercase" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Rabatt (€)</label>
              <input type="number" step="0.5" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)}
                data-testid="checkout-discount"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
            </div>
          </div>

          <div className="border-t border-[#1A1A1A] pt-4 flex items-baseline justify-between">
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#A8A398]">Zu zahlen</div>
            <div className="font-heading text-3xl text-[#D4AF37]" data-testid="checkout-total">{total.toFixed(2)} €</div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#1A1A1A] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#A8A398] hover:text-[#F5F0E6]" data-testid="checkout-cancel">
            <XCircle size={14} className="inline mr-1" /> Abbrechen
          </button>
          <button onClick={submit} disabled={busy}
            data-testid="checkout-confirm"
            className="bg-[#D4AF37] text-black px-6 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82] transitions-safe disabled:opacity-40 inline-flex items-center gap-2">
            <Check size={14} /> {busy ? "Kassiere …" : "Kassieren & Ticket ausgeben"}
          </button>
        </div>
      </div>
    </div>
  );
}
